import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Modal from "../components/Modal";
import SEOHead from "../components/SEOHead";
import {
  ArrowRight,
  Zap,
  Shield,
  Briefcase,
  Wrench,
  Truck,
  Award,
  CheckCircle2,
} from "lucide-react";

const oilPlatforms = "/images/oil-platforms.jpeg";
const industrialPipeline = "/images/industrial-pipeline.jpeg";
const constructionWorker = "/images/Alkanes.png";

const services = [
  {
    id: 1,
    title: "Engineering",
    description:
      "Expert engineering design and consultation for Oil & Gas, Power, and Infrastructure projects.",
    icon: Zap,
    color: "from-blue-400 to-cyan-400",
  },
  {
    id: 2,
    title: "Procurement",
    description:
      "Efficient procurement of equipment and materials with quality assurance and cost optimization.",
    icon: Briefcase,
    color: "from-purple-400 to-pink-400",
  },
  {
    id: 3,
    title: "Construction",
    description:
      "Full-scale construction and installation services with adherence to safety and quality standards.",
    icon: Wrench,
    color: "from-orange-400 to-red-400",
  },
  {
    id: 4,
    title: "Installation",
    description:
      "Professional installation services ensuring optimal performance and regulatory compliance.",
    icon: Truck,
    color: "from-green-400 to-emerald-400",
  },
  {
    id: 5,
    title: "Equipment Leasing",
    description:
      "Flexible equipment leasing solutions tailored to project-specific requirements.",
    icon: Shield,
    color: "from-indigo-400 to-purple-400",
  },
  {
    id: 6,
    title: "Safety Equipment Supply",
    description:
      "Comprehensive safety equipment and protective gear for workplace protection.",
    icon: Award,
    color: "from-yellow-400 to-orange-400",
  },
];

const stats = [
  { number: "500+", label: "Projects Completed" },
  { number: "15+", label: "Years Experience" },
  { number: "100+", label: "Team Members" },
  { number: "98%", label: "Client Satisfaction" },
];

export default function Index() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950">
      <SEOHead
        title="Alkanes Engineering Limited - Engineering Services Nigeria | Oil & Gas Solutions Lagos"
        description="Alkanes Engineering Limited provides expert engineering services in Lagos, Nigeria. Oil & Gas, Power, Infrastructure, Construction, Procurement, Installation, Equipment Leasing, and Safety Equipment Supply."
        keywords="Alkanes Engineering, Alkanes Engineering Limited, engineering services Nigeria, oil and gas engineering, engineering company Lagos, construction services, equipment procurement"
        canonical="https://alkanesengineering.com"
      />
      <Header />

      {/* Hero Section */}
      <section
        className="relative overflow-hidden pt-20 pb-24 md:pt-28 md:pb-32"
        style={{
          backgroundImage: `url(${oilPlatforms})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Transparent Dark Overlay */}
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`max-w-3xl transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
          >
            {/* Left Content */}
            <div className="space-y-6">
              <div className="inline-block">
                <span className="inline-block bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Engineering Solutions You Can Trust
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
                Professional Engineering Solutions
              </h1>
              <p className="text-lg text-gray-100 leading-relaxed max-w-xl">
                Alkane Engineering Limited delivers comprehensive engineering,
                procurement, and construction services across Oil & Gas, Power,
                and Infrastructure sectors with proven expertise and commitment
                to safety.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-3 rounded-lg font-semibold transition-all hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-1 active:scale-95"
                >
                  Explore Services <ArrowRight size={20} />
                </Link>
                <button
                  onClick={() => setIsConsultationOpen(true)}
                  className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all hover:bg-white hover:text-primary"
                >
                  Get Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section
        className="relative py-12 md:py-16 border-y border-gray-800"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gray-950/85"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <p className="text-gray-300 text-sm md:text-base">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        className="relative py-20 md:py-28"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gray-950/85"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Our Core Services
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Comprehensive engineering solutions tailored to your project needs
              across all sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <button
                  key={service.id}
                  onClick={() => setIsConsultationOpen(true)}
                  onMouseEnter={() => setHoveredService(service.id)}
                  onMouseLeave={() => setHoveredService(null)}
                  className={`group relative rounded-2xl border-2 p-8 transition-all duration-300 cursor-pointer text-left ${
                    hoveredService === service.id
                      ? "border-primary shadow-2xl shadow-primary/15 -translate-y-2 bg-gray-900"
                      : "border-gray-700 hover:border-primary/50 bg-gray-800"
                  }`}
                  style={{
                    animationDelay: `${index * 50}ms`,
                  }}
                >
                  {/* Gradient Background on Hover */}
                  {hoveredService === service.id && (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-2xl pointer-events-none"></div>
                  )}

                  {/* Content */}
                  <div className="relative space-y-4">
                    <div
                      className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${service.color} text-white transition-all group-hover:scale-110`}
                    >
                      <Icon size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {service.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2 text-primary font-semibold pt-4 group-hover:gap-3 transition-all">
                      Learn More <ArrowRight size={20} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section
        className="relative py-16 md:py-24 overflow-hidden"
        style={{
          backgroundImage: `url(${constructionWorker})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 backdrop-blur-sm bg-gray-950/50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Why Choose Alkane Engineering
              </h2>
              <ul className="space-y-4">
                {[
                  "Local expertise with global standards",
                  "Strong HSE culture and compliance",
                  "Proven track record of successful projects",
                  "Competitive pricing and flexible terms",
                  "Dedicated project teams",
                  "Full lifecycle support",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-primary/20 to-primary/10 rounded-3xl h-96 border-2 border-primary/40 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">🏆</div>
                <p className="text-lg font-semibold text-white">
                  Industry Excellence
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative text-white py-16 md:py-24"
        style={{
          backgroundImage: `url(${oilPlatforms})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary/85"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold text-white">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-gray-100 max-w-2xl mx-auto">
            Get in touch with our team to discuss your engineering needs and
            discover how we can deliver solutions that drive your success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <button
              onClick={() => setIsConsultationOpen(true)}
              className="bg-white text-primary px-8 py-3 rounded-lg font-semibold text-lg transition-all hover:shadow-xl hover:-translate-y-1 active:scale-95"
            >
              Contact Us Today
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold text-lg transition-all hover:bg-white hover:text-primary">
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Consultation Modal */}
      <Modal
        isOpen={isConsultationOpen}
        onClose={() => setIsConsultationOpen(false)}
        title="Request Consultation"
        size="md"
      >
        <div className="space-y-6">
          <p className="text-gray-600 text-lg">
            Tell us about your project and we'll connect you with the right team
            to help you succeed.
          </p>
          <form className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  placeholder="John"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  placeholder="Doe"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                placeholder="john@example.com"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Company
              </label>
              <input
                type="text"
                placeholder="Your company name"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Service Required
              </label>
              <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                <option>Select a service</option>
                <option>Engineering</option>
                <option>Procurement</option>
                <option>Construction</option>
                <option>Installation</option>
                <option>Equipment Leasing</option>
                <option>Safety Equipment</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Message
              </label>
              <textarea
                placeholder="Tell us about your project..."
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              ></textarea>
            </div>
            <button
              type="submit"
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-primary/40 active:scale-95"
            >
              Send Consultation Request
            </button>
          </form>
        </div>
      </Modal>

      <Footer />
    </div>
  );
}
